import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import './LastSeasonBanner.css';

type ConfettiFn = (opts: object) => void;
let _confetti: ConfettiFn | null = null;
async function ensureConfetti(): Promise<ConfettiFn | null> {
  if (_confetti) return _confetti;
  try {
    const mod = await import('canvas-confetti');
    _confetti = mod.default as unknown as ConfettiFn;
    return _confetti;
  } catch {
    return null;
  }
}

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const STORAGE_KEY = 'season_end_seen_v2';

interface Summary {
  season_number: number;
  end_date: string;
  final_rating: number;
  rank: number | null;
  tier: string | null;
  badge_code: string | null;
}

/** Web Audio API ile kısa sentetik fanfar (MP3 yoksa fallback) */
function playFanfareSynth() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.22, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.38);
      osc.start(t);
      osc.stop(t + 0.38);
    });
  } catch {
    // ignore
  }
}

/** Ses çal: önce MP3 dener, hata/yoksa sentetik */
function playSound() {
  try {
    const audio = new Audio('/sounds/season_end.wav');
    audio.volume = 0.6;
    audio.play().catch(() => playFanfareSynth());
  } catch {
    playFanfareSynth();
  }
}

/** canvas-confetti patlaması */
async function fireConfetti() {
  const confetti = await ensureConfetti();
  if (!confetti) return;
  const colors = ['#facc15', '#a855f7', '#22d3ee', '#f97316', '#34d399'];
  setTimeout(() => {
    confetti({ angle: 60,  spread: 70, particleCount: 80, origin: { x: 0.1, y: 0.5 }, colors });
    confetti({ angle: 120, spread: 70, particleCount: 80, origin: { x: 0.9, y: 0.5 }, colors });
  }, 400);
  setTimeout(() => {
    confetti({ spread: 100, particleCount: 60, origin: { x: 0.5, y: 0.35 }, colors, scalar: 0.8 });
  }, 700);
}

const STARS = ['⭐', '✨', '🌟', '💫'];
const STAR_POS = [
  { top: '10%', left: '8%', delay: '0s' },
  { top: '15%', right: '10%', delay: '0.5s' },
  { top: '70%', left: '6%', delay: '1s' },
  { top: '75%', right: '8%', delay: '1.5s' },
  { top: '40%', left: '4%', delay: '0.8s' },
  { top: '45%', right: '5%', delay: '1.2s' },
];

export function LastSeasonBanner() {
  const { token } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState<Summary | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (!token) { setSummary(null); return; }
    let aborted = false;
    fetch(`${API}/stats/season/last-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (aborted || !d?.summary) return;
        // Daha önce görüldüyse gösterme
        try {
          const seen = localStorage.getItem(STORAGE_KEY);
          if (seen === String(d.summary.season_number)) return;
        } catch {}
        setSummary(d.summary);
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [token]);

  // Efektler: modal görününce 1 kez tetikle
  useEffect(() => {
    if (!summary || fired.current) return;
    fired.current = true;
    // 450ms sonra (popIn bitmeden)
    setTimeout(() => {
      playSound();
      fireConfetti();
    }, 300);
  }, [summary]);

  if (!summary) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(summary.season_number)); } catch {}
    setSummary(null);
    fired.current = false;
  };

  const profileT: any = (t as any).profile || {};
  const homeT: any = (t as any).home || {};

  const tierLabel =
    summary.tier === 'top10'
      ? (profileT.seasonRewardTop10 ?? 'Top 10 — 💎 Elmas Rozet')
      : summary.tier === 'top100'
      ? (profileT.seasonRewardTop100 ?? 'Top 100 — 🥇 Altın Rozet')
      : summary.tier === 'top1000'
      ? (profileT.seasonRewardTop1000 ?? 'Top 1000 — 🥈 Gümüş Rozet')
      : null;

  const headline = summary.rank
    ? (homeT.lastSeasonRank ?? 'Sezon {n} — #{rank}. sırada bitirdin!')
        .replace('{n}', String(summary.season_number))
        .replace('{rank}', String(summary.rank))
    : (homeT.lastSeasonNoRank ?? 'Sezon {n} bitti! Final puanı: {rating}')
        .replace('{n}', String(summary.season_number))
        .replace('{rating}', String(summary.final_rating));

  const ctaText = (homeT.seasonEndCta ?? 'Harika! 🎉');

  return (
    <div className="season-end-overlay" role="dialog" aria-modal="true">
      <div className="season-end-card">
        {/* Floating stars */}
        <div className="season-end-stars" aria-hidden="true">
          {STAR_POS.map((pos, i) => (
            <span
              key={i}
              className="season-end-star"
              style={{ ...pos, animationDelay: pos.delay } as React.CSSProperties}
            >
              {STARS[i % STARS.length]}
            </span>
          ))}
        </div>

        <button className="season-end-close" onClick={handleDismiss} aria-label="Kapat">✕</button>

        <span className="season-end-trophy" aria-hidden="true">🏆</span>

        <div className="season-end-label">
          {(homeT.lastSeasonTitle ?? 'Sezon {n} Bitti! 🎉').replace('{n}', String(summary.season_number))}
        </div>

        {summary.rank && (
          <div className="season-end-headline">{headline}</div>
        )}

        <div className="season-end-rating">
          <span className="season-end-rating-icon">⭐</span>
          {summary.final_rating} ELO
        </div>

        {tierLabel && (
          <div className={`season-end-tier season-end-tier--${summary.tier}`}>
            {tierLabel}
          </div>
        )}

        {summary.rank && (
          <div className="season-end-rank">{t.profile.rankSuffix.replace('{rank}', String(summary.rank))}</div>
        )}

        <button className="season-end-cta" onClick={handleDismiss}>
          {ctaText}
        </button>
      </div>
    </div>
  );
}
